import clsx from 'clsx';
import Image from 'next/image';

import { IBlogPost, INews } from 'constants/types';

import { formatCreatedAtDate } from 'utils/formatDays';

import { Icon } from 'common/icon';
import { LoadMoreButton } from 'components/load-more-button';
import { Header } from 'components/slider/header';
import { Routes } from 'constants/routes';
import Link from 'next/link';
import { useMemo, useState } from 'react';
import styles from './styles.module.css';

type TextSize = 'small' | 'medium';
type WidthImage = 'box' | 'long';
type ArticleVariant =
  | 'base'
  | 'exetended'
  | 'reverseBase'
  | 'topLongMainNews'
  | 'bottomLongMainNews';

export const Article = ({
  data,
  className,
  maxLines = 3,
  variant,
  category,
  textSize,
  arrow,
  widthImage,
  isArrowCenter,
  isArrowTop,
}: {
  data: INews;
  className?: string;
  maxLines?: number;
  variant?: ArticleVariant;
  category?: string;
  textSize: TextSize;
  arrow: boolean;
  widthImage: WidthImage;
  isArrowCenter?: boolean;
  isArrowTop?: boolean;
}) => {
  const dynamicStyle = {
    WebkitLineClamp: maxLines,
  };
  const fontSize = useMemo(() => {
    switch (textSize) {
      case 'small':
        return styles.fontSizeSmall;
      case 'medium':
        return styles.fontSizeMedium;
    }
  }, [textSize]);

  const imageSize = useMemo(() => {
    switch (widthImage) {
      case 'box':
        return styles.article_card__image_box;
      case 'long':
        return styles.article_card__image_long;
    }
  }, [widthImage]);

  const dynamicClass = useMemo(() => {
    switch (variant) {
      case 'topLongMainNews':
        return {
          container: styles.news_card__info_container_topLongMainNews,
          margin: styles.article_card__wrapper_topLongMainNews,
        };
      case 'bottomLongMainNews':
        return {
          container: styles.news_card__info_container_topLongMainNews,
          margin: styles.article_card__wrapper_bottomLongMainNews,
        };
    }
  }, [variant]);

  return (
    <Link
      prefetch={false}
      href={Routes.dynamicBlogPage(data)}
      key={data?._id?.toString()}
      className={clsx(styles.news__article, className)}
    >
      <div className={clsx(styles.article_card__wrapper, dynamicClass?.margin)}>
        <div className={clsx(styles.article_card__image, imageSize)}>
          <Image
            width={184}
            height={135}
            src={data?.imageUrl ?? ''}
            alt={`article-${data._id}`}
          />
        </div>

        <div className={styles.article_card__content}>
          <div
            className={clsx(
              styles.news_card__info_container,
              fontSize,
              dynamicClass?.container
            )}
          >
            <div className={styles.dateAndCategory}>
              {formatCreatedAtDate(data.last_modified)}
              {category && (
                <>
                  <div className={styles.divider} />
                  <span>{category}</span>
                </>
              )}
            </div>
            {arrow && isArrowTop && (
              <div className={styles.icon_wrapper}>
                <Icon.Arrow width={30} height={30} fillOpacity="0.4" />
              </div>
            )}
          </div>
          <div className={styles.article_card__link_wrapper}>
            <span style={dynamicStyle} className={styles.article_card__link}>
              {data.title}
            </span>
          </div>
        </div>
        {arrow && isArrowCenter && (
          <div className={styles.icon_wrapper}>
            <Icon.Arrow width={30} height={30} fillOpacity="0.4" />
          </div>
        )}
      </div>
    </Link>
  );
};

type PropType = {
  name?: string;
  news: INews;
  arrow: boolean;
  variant: ArticleVariant;
};

export const MainNews = (props: PropType) => {
  const { news, arrow, variant } = props;

  const dynamicContent = useMemo(() => {
    if (!news) {
      return null;
    }

    switch (variant) {
      case 'base':
        return (
          <div className={styles.mainNews__base}>
            {news?.imageUrl ? (
              <div className={styles.mainNews__imageWrap}>
                <Image
                  fill
                  alt={news.title}
                  src={news.imageUrl}
                  className={styles.img}
                  sizes="(max-width: 860px) 100vw, 468px"
                />
              </div>
            ) : null}
            <div className={styles.mainNews__content_base}>
              <div className={styles.mainNews__title_base}>{news.title}</div>
              {arrow && (
                <div className={styles.icon_wrapper}>
                  <Icon.Arrow width={30} height={30} fillOpacity="0.4" />
                </div>
              )}
            </div>
          </div>
        );
      case 'reverseBase':
      case 'exetended':
        return (
          <div>
            <div className={styles.imageBg}>
              <div
                className={clsx(
                  styles.imgContainer,
                  styles.imgContainer_exetended_reverseBase
                )}
              >
                <Image
                  width={385}
                  height={290}
                  alt={news.title}
                  src={news?.imageUrl ?? ''}
                  className={styles.img}
                />
              </div>
            </div>
            <div className={styles.mainNews__content_exetended}>
              <div className={styles.mainNews__wrapper}>
                <div className={styles.news_card__info_container}>
                  {formatCreatedAtDate(news.last_modified)}
                  {news.category && (
                    <>
                      <div className={styles.divider} />
                      <span>{news.category}</span>
                    </>
                  )}
                </div>
                <div className={styles.mainNews__title_exetended}>
                  {news.title}
                </div>
              </div>
              {arrow && (
                <div className={styles.icon_wrapper}>
                  <Icon.Arrow width={30} height={30} fillOpacity="0.4" />
                </div>
              )}
            </div>
          </div>
        );
      default:
        return null;
    }
  }, [arrow, news, variant]);

  if (!news) return null;

  return (
    <div
      className={clsx(styles.mainNews__container, {
        [styles.mainNews__container_padding_base]: variant === 'base',
        [styles.mainNews__container_padding_exetended]: variant === 'exetended',
        [styles.mainNews__container_padding_reverseBase]:
          variant === 'reverseBase',
      })}
    >
      <div className={styles.news__column_b}>
        <div className={styles.mainNews_link}>
          <Link
            prefetch={false}
            key={news._id}
            href={Routes.dynamicBlogPage(news)}
            className={styles.mainNews_link}
          >
            {dynamicContent}
          </Link>
        </div>
      </div>
    </div>
  );
};
interface Props {
  data: INews[] | IBlogPost[];
  title: string;
  expandFull?: boolean;
  maxLines?: number;
  anchor?: string;
  url?: string;
  arrow: boolean;
  variant: ArticleVariant;
  hasIcon?: boolean;
  count?: number;
  urlHeader?: string;
  headerIsBigName?: boolean;
  headerIsSmallName?: boolean;
}

export const News = ({
  data,
  title,
  expandFull,
  maxLines = 3,
  anchor,
  url = Routes.blogAndNews,
  arrow,
  variant,
  hasIcon,
  count,
  urlHeader,
  headerIsBigName,
  headerIsSmallName,
}: Props) => {
  const [page, setPage] = useState(1);

  const pageLength = 4;

  const visibleItems = page * pageLength;
  const isShowMoreVisible = Boolean(data?.length && data.length > visibleItems);

  const chunkArray = (array: any[], chunkSize: number): any[][] => {
    const chunks = [];
    for (let i = 0; i < array.length; i += chunkSize) {
      chunks.push(array.slice(i, i + chunkSize));
    }
    return chunks;
  };

  const visibleData = data?.slice(0, visibleItems) ?? [];
  const groups = chunkArray(
    visibleData?.slice(pageLength, visibleData?.length),
    2
  );

  const dynamicContent = useMemo(() => {
    if (!data?.length) {
      return null;
    }

    switch (variant) {
      case 'base':
      case 'exetended':
        return (
          <>
            <Header
              name={title}
              hasIcon={hasIcon}
              count={count}
              url={urlHeader}
              isBigName={headerIsBigName}
              isSmallName={headerIsSmallName}
            />
            <div className={styles.news__row}>
              <MainNews news={data[0]} arrow={arrow} variant={variant} />
              <div className={styles.news__column}>
                <div className={styles.news__items}>
                  {data
                    .slice(1, pageLength)
                    ?.map((item: INews) => (
                      <Article
                        maxLines={maxLines}
                        data={item}
                        key={item._id}
                        textSize={'small'}
                        arrow={arrow}
                        isArrowCenter
                        widthImage={'box'}
                        category={item.category}
                      />
                    ))}
                </div>
              </div>
            </div>
            {groups.map((group, groupIndex) => (
              <div
                key={`group-${groupIndex}-${page}`}
                className={clsx(styles.news__row, {
                  [styles.news__row_gap_base]: variant === 'base',
                  [styles.news__row_gap_not_base]: variant !== 'base',
                })}
              >
                {group.map((item: INews) => (
                  <Article
                    data={item}
                    key={item._id}
                    maxLines={maxLines}
                    className={styles.news__article_flex}
                    textSize={'small'}
                    arrow={arrow}
                    isArrowCenter
                    widthImage={'box'}
                    category={item.category}
                  />
                ))}
              </div>
            ))}
          </>
        );
      case 'reverseBase':
        return (
          <>
            <Header
              name={title}
              hasIcon={hasIcon}
              count={count}
              url={urlHeader}
              isBigName={headerIsBigName}
              isSmallName={headerIsSmallName}
            />
            <div className={styles.news__row}>
              <div className={styles.news__column}>
                <div className={styles.news__items}>
                  {data
                    .slice(1, pageLength)
                    ?.map((item: INews) => (
                      <Article
                        maxLines={maxLines}
                        data={item}
                        key={item._id}
                        textSize={'small'}
                        arrow={arrow}
                        isArrowCenter
                        widthImage={'box'}
                        category={item.category}
                      />
                    ))}
                </div>
              </div>
              <MainNews news={data[0]} arrow={arrow} variant={variant} />
            </div>
            {groups.map((group, groupIndex) => (
              <div
                key={groupIndex}
                className={clsx(
                  styles.news__row,
                  styles.news__row_gap_not_base
                )}
              >
                {group.map((item: INews) => (
                  <Article
                    data={item}
                    key={item._id}
                    maxLines={maxLines}
                    className={styles.news__article_flex}
                    textSize={'small'}
                    arrow={arrow}
                    isArrowCenter
                    widthImage={'box'}
                    category={item.category}
                  />
                ))}
              </div>
            ))}
          </>
        );
      case 'topLongMainNews':
        return (
          <>
            <Header
              name={title}
              hasIcon={hasIcon}
              count={count}
              url={urlHeader}
            />
            <Article
              data={data[0]}
              arrow={arrow}
              isArrowTop
              variant={variant}
              textSize={'small'}
              widthImage={'long'}
              category={data[0].category}
            />
            <div className={styles.news__column}>
              <div className={styles.gridContainer_topLongMainNews}>
                {data
                  .slice(1, pageLength + 1)
                  ?.map((item: INews) => (
                    <Article
                      maxLines={maxLines}
                      data={item}
                      key={item._id}
                      textSize={'small'}
                      arrow={arrow}
                      isArrowCenter
                      widthImage={'box'}
                      category={item.category}
                    />
                  ))}
              </div>
            </div>
            {groups.map((group, groupIndex) => (
              <div
                key={groupIndex}
                className={clsx(
                  styles.news__row,
                  styles.news__row_gap_not_base
                )}
              >
                {group.map((item: INews) => (
                  <Article
                    data={item}
                    key={item._id}
                    maxLines={maxLines}
                    className={styles.news__article_flex}
                    textSize={'small'}
                    isArrowCenter
                    arrow={arrow}
                    widthImage={'box'}
                    category={item.category}
                  />
                ))}
              </div>
            ))}
          </>
        );
      case 'bottomLongMainNews':
        return (
          <>
            <Header
              name={title}
              hasIcon={hasIcon}
              count={count}
              url={urlHeader}
            />
            <div className={styles.news__column}>
              <div className={styles.gridContainer_topLongMainNews}>
                {data
                  .slice(1, pageLength + 1)
                  ?.map((item: INews) => (
                    <Article
                      maxLines={maxLines}
                      data={item}
                      key={item._id}
                      textSize={'small'}
                      arrow={arrow}
                      isArrowCenter
                      widthImage={'box'}
                      category={item.category}
                    />
                  ))}
              </div>
            </div>
            <Article
              data={data[0]}
              arrow={arrow}
              isArrowTop
              variant={variant}
              textSize={'small'}
              widthImage={'long'}
              category={data[0].category}
            />
            {groups.map((group, groupIndex) => (
              <div
                key={groupIndex}
                className={clsx(
                  styles.news__row,
                  styles.news__row_gap_not_base
                )}
              >
                {group.map((item: INews) => (
                  <Article
                    data={item}
                    key={item._id}
                    maxLines={maxLines}
                    className={styles.news__article_flex}
                    textSize={'small'}
                    arrow={arrow}
                    isArrowCenter
                    widthImage={'box'}
                    category={item.category}
                  />
                ))}
              </div>
            ))}
          </>
        );
      default:
        return null;
    }
  }, [
    arrow,
    count,
    data,
    groups,
    hasIcon,
    headerIsBigName,
    headerIsSmallName,
    maxLines,
    page,
    title,
    urlHeader,
    variant,
  ]);

  if (!data?.length) {
    return null;
  }

  return (
    <div className={styles.container} id={anchor}>
      {dynamicContent}

      {isShowMoreVisible && (
        <div className={styles.show_more_container}>
          <LoadMoreButton
            isAuto={false}
            isVisible={true}
            onClick={() => {
              if (expandFull) {
                setPage(Math.ceil(data.length / pageLength));
              } else {
                setPage(
                  Math.min(page + 1, Math.ceil(data.length / pageLength))
                );
              }
            }}
            withIcon={false}
            variant={'showMore'}
            description="Show more"
            isFetching={false}
            className={styles.loadmoreButton}
          />
        </div>
      )}

      {!isShowMoreVisible && <div style={{ height: 24 }} />}
    </div>
  );
};
